import { formatLocaleDate } from "src/localization/formatLocaleDate";
import type { SupportedLocale } from "src/localization/dateFormats";

export const WORKFLOW_STAGE_CONFIG = {
  draft: {
    label: "Draft",
    badgeVariant: "warning",
    barBg: "warningLightest",
    barText: "warningDark",
  },
  active: {
    label: "Active",
    badgeVariant: "success",
    barBg: "successLightest",
    barText: "successDark",
  },
  review: {
    label: "Review",
    badgeVariant: "default",
    barBg: "primaryLightest",
    barText: "primaryDark",
  },
  approved: {
    label: "Approved",
    badgeVariant: "success",
    barBg: "primaryLighter",
    barText: "primaryDark",
  },
  implemented: {
    label: "Implemented",
    badgeVariant: "success",
    barBg: "successLighter",
    barText: "successDark",
  },
  closed: {
    label: "Closed",
    badgeVariant: "default",
    barBg: "surfaceBorder",
    barText: "subText",
  },
} as const;

export const DESIGN_TYPE_CONFIG = {
  delivery_org_design: {
    label: "Delivery",
    badgeVariant: "success",
  },
  supervisory_org_design: {
    label: "Supervisory",
    badgeVariant: "secondary",
  },
} as const;

export const PRIMARY_DRIVER_OPTIONS = {
  supervisory_org_design: [
    "Clarify accountability and decision rights",
    "Increase managerial effectiveness",
    "Support growth or restructuring",
    "Reduce hierarchy cost and complexity",
    "Leadership change or succession",
  ],
  delivery_org_design: [
    "Improve speed and flow of work",
    "Strengthen cross-functional execution",
    "Align around customer, product, or value stream",
    "Build new capabilities or ways of working",
    "Improve adaptability and resilience",
  ],
} as const;

export type DesignSummary = {
  id?: string;
  name?: string;
  workflowStage?: string;
  startDate?: string;
  targetImplementationDate?: string;
  designType?: string;
  purpose?: string;
  updatedAt?: string;
};

type WorkflowStageKey = keyof typeof WORKFLOW_STAGE_CONFIG;
type DesignTypeKey = keyof typeof DESIGN_TYPE_CONFIG;

export const formatDate = (
  dateStr: string | null | undefined,
  locale?: SupportedLocale | string | null
): string | null => formatLocaleDate(dateStr, locale, "readable");

export const formatRelativeDate = (
  dateStr: string | null | undefined,
  locale?: SupportedLocale | string | null
): string | null => {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return formatDate(dateStr, locale);
  } catch {
    return null;
  }
};

export const truncate = (
  str: string | null | undefined,
  max = 80
): string | null => {
  if (!str) return null;
  return str.length <= max ? str : `${str.slice(0, max)}\u2026`;
};

export const getWorkflowStageConfig = (stage?: string | null) => {
  const key = (stage || "draft").toLowerCase();
  if (key in WORKFLOW_STAGE_CONFIG) {
    return WORKFLOW_STAGE_CONFIG[key as WorkflowStageKey];
  }
  return WORKFLOW_STAGE_CONFIG.draft;
};

export const getDesignTypeConfig = (type?: string | null) =>
  type && type in DESIGN_TYPE_CONFIG
    ? DESIGN_TYPE_CONFIG[type as DesignTypeKey]
    : null;

export const sortByName = (a: DesignSummary, b: DesignSummary) =>
  (a.name || "").localeCompare(b.name || "", undefined, { numeric: true });

export const groupByStage = (designs: DesignSummary[]) => {
  const groups = new Map<string, DesignSummary[]>();
  Object.keys(WORKFLOW_STAGE_CONFIG).forEach((key) => groups.set(key, []));
  designs.forEach((design) => {
    const key = (design.workflowStage || "draft").toLowerCase();
    const bucket = groups.get(key) || groups.get("draft");
    if (bucket) {
      bucket.push(design);
    }
  });
  for (const [key, list] of groups) {
    if (list.length === 0) groups.delete(key);
  }
  return groups;
};

const buildMonths = (
  start: Date,
  end: Date,
  locale?: SupportedLocale | string | null
) => {
  const months: Array<{
    date: Date;
    label: string;
    year: number;
    isJan: boolean;
  }> = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const monthLocale = locale ?? "en-AU";
  while (cursor <= end) {
    months.push({
      date: new Date(cursor),
      label: cursor.toLocaleDateString(monthLocale, { month: "short" }),
      year: cursor.getFullYear(),
      isJan: cursor.getMonth() === 0,
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
};

export const getTimelineBounds = (
  designs: DesignSummary[],
  locale?: SupportedLocale | string | null
) => {
  const now = new Date();
  const dates = [now];
  designs.forEach((design) => {
    if (design.startDate) dates.push(new Date(design.startDate));
    if (design.targetImplementationDate) {
      dates.push(new Date(design.targetImplementationDate));
    }
  });

  const minTs = Math.min(...dates.map((d) => d.getTime()));
  const maxTs = Math.max(...dates.map((d) => d.getTime()));
  const minDate = new Date(
    new Date(minTs).getFullYear(),
    new Date(minTs).getMonth() - 1,
    1
  );
  const maxDate = new Date(
    new Date(maxTs).getFullYear(),
    new Date(maxTs).getMonth() + 2,
    0
  );

  return { minDate, maxDate, months: buildMonths(minDate, maxDate, locale) };
};

export const dateToPercent = (
  date: Date | string,
  minDate: Date,
  maxDate: Date
) => {
  const d = new Date(date);
  const range = maxDate.getTime() - minDate.getTime();
  if (range === 0) return 50;
  return ((d.getTime() - minDate.getTime()) / range) * 100;
};

export const hasAnyDate = (design: DesignSummary) =>
  Boolean(design.startDate || design.targetImplementationDate);

export const startOfLocalDay = (d: Date | string) => {
  const x = new Date(d);
  return new Date(x.getFullYear(), x.getMonth(), x.getDate());
};

const TERMINAL_WORKFLOW_STAGES = ["closed"] as const;

export const categorizeNowNextLater = (designs: DesignSummary[]) => {
  const todayStart = startOfLocalDay(new Date());
  const buckets: {
    now: DesignSummary[];
    next: DesignSummary[];
    later: DesignSummary[];
  } = { now: [], next: [], later: [] };

  designs.forEach((design) => {
    const stage = (design.workflowStage || "").toLowerCase();
    if (TERMINAL_WORKFLOW_STAGES.includes(stage as "closed")) {
      return;
    }

    const start = design.startDate ? new Date(design.startDate) : null;
    const target = design.targetImplementationDate
      ? new Date(design.targetImplementationDate)
      : null;
    const startDay = start ? startOfLocalDay(start) : null;
    const targetDay = target ? startOfLocalDay(target) : null;

    if (!start && !target) {
      buckets.later.push(design);
      return;
    }
    if (startDay && startDay.getTime() > todayStart.getTime()) {
      buckets.next.push(design);
      return;
    }
    if (!startDay && targetDay && targetDay.getTime() > todayStart.getTime()) {
      buckets.next.push(design);
      return;
    }
    if (targetDay && targetDay.getTime() < todayStart.getTime()) {
      buckets.now.push(design);
      return;
    }
    buckets.now.push(design);
  });

  const sortByTarget = (a: DesignSummary, b: DesignSummary) => {
    const aDate = a.targetImplementationDate
      ? new Date(a.targetImplementationDate).getTime()
      : Infinity;
    const bDate = b.targetImplementationDate
      ? new Date(b.targetImplementationDate).getTime()
      : Infinity;
    return aDate - bDate;
  };

  buckets.now.sort(sortByTarget);
  buckets.next.sort(sortByTarget);
  buckets.later.sort(sortByName);

  return buckets;
};
